/**
 * Who answers a chat turn — WP-BR-21, the browser half of WP-BR-20.
 *
 * `POST /api/v1/ai/chat` takes an optional `provider`. Absent and `'litellm'` are the
 * same branch on the server (`ChatRequestSchema` writes no `.default()` on purpose),
 * so this screen sends the member only when the operator has actually chosen the
 * other one: a body that always carried `provider: 'litellm'` would be this client
 * asserting a routing decision it did not make.
 *
 * ── Two answers, and they are not the same shape ─────────────────────────────
 *
 * `litellm` answers `AiChatResponse` — summary, confidence, limitations, evidence —
 * and is rendered by `InsightCard`, the one renderer. `brain` answers
 * `AiChatBrainResponse`: `provider`, the Control Plane's `run_id`, and `reply`
 * **passed through and never interpreted**, because the shape belongs to WCC and
 * `openapi.yaml` deliberately promises nothing about it. So `reply` is `unknown` here
 * too, and a brain turn renders as a plain reply block rather than being poured into
 * a card whose confidence, limitations and evidence regions the brain never filled.
 * Faking those four from a passthrough body is precisely the caveat-losing renderer
 * `screens/ai/one-renderer.test.tsx` exists to prevent.
 *
 * ── The one refusal that is about the option, not about the question ─────────
 *
 * `503 WORKFORCE_AI_UNAVAILABLE` with `details.reason === 'control_plane_not_configured'`
 * means this deployment was assembled without a Control Plane client — there is no
 * BrainAI to reach, today or on a retry. It is the only refusal on this path that is
 * a fact about the *option*, so it is the only one that disables it; every other one
 * (upstream down, rate limited, brain refused) leaves the option alone and shows the
 * server's own sentence, because those are facts about this turn.
 */

import type { AiChatRequest, AiChatResponse } from '../../api/types';
import { isWorkforceApiError } from '../../api/errors';

export const AI_CHAT_PROVIDERS = ['litellm', 'brain'] as const;

export type AiChatProvider = (typeof AI_CHAT_PROVIDERS)[number];

/** `AiChatRequest` plus the routing member. Omitted entirely for `litellm`. */
export type AiChatTurnRequest = AiChatRequest & { readonly provider?: AiChatProvider };

/** `AiChatBrainResponse`. `reply` is the upstream body, uninterpreted. */
export interface AiChatBrainResponse {
  readonly provider: 'brain';
  readonly run_id: string;
  readonly reply: unknown;
}

export type AiChatTurnResponse = AiChatResponse | AiChatBrainResponse;

/** Which of the two shapes arrived. Keyed on `provider`, which only the brain sends. */
export function isBrainAnswer(value: AiChatTurnResponse | null): value is AiChatBrainResponse {
  if (value === null) return false;
  // Read off a widened view rather than through `'provider' in value`: the `in`
  // narrows at type level and then the compiler calls the runtime comparison dead,
  // while the value on the wire is whatever the server actually sent.
  return (value as { readonly provider?: unknown }).provider === 'brain';
}

/**
 * The brain's sentence, when the passthrough body carries an obvious one.
 *
 * Four keys are tried and nothing is invented: a body this function does not
 * recognise answers `null`, and the panel prints the JSON verbatim instead of a
 * guess. `[object Object]` on a screen where a person expects an answer is the
 * failure this avoids; so is picking a nested field because it happened to be a
 * string on the day this was written.
 */
export function brainReplyText(reply: unknown): string | null {
  if (typeof reply === 'string') return reply.trim() === '' ? null : reply;
  if (typeof reply !== 'object' || reply === null) return null;
  const record = reply as Readonly<Record<string, unknown>>;
  for (const key of ['answer', 'reply', 'text', 'content']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return null;
}

/** True for the one refusal that means there is no BrainAI to reach at all. */
export function brainIsUnconfigured(error: unknown): boolean {
  if (!isWorkforceApiError(error)) return false;
  return (
    error.status === 503 &&
    error.code === 'WORKFORCE_AI_UNAVAILABLE' &&
    error.details.reason === 'control_plane_not_configured'
  );
}
